import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import "./css/fileManager.css";
import natureBg from "./img/Foto2wilmanatur.jpg";
import { useNavigate } from "react-router-dom";
import { listFiles, uploadFile, deleteFile, deleteFolder } from "./api/filesApi";
import { useLang } from "./hooks/useLang";
import { DICT } from "./i18n";

function FileManagerPage() {
  const [files, setFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState("/");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadMode, setUploadMode] = useState("file"); 
  const [uploadTags, setUploadTags] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const [selectedTags, setSelectedTags] = useState([]); 
  const [tagMatchMode, setTagMatchMode] = useState("any"); 
  const [tagFilterOpen, setTagFilterOpen] = useState(false);
  const navigate = useNavigate();
  const safeFiles = useMemo(() => (Array.isArray(files) ? files : []), [files]);


  const [lang] = useLang();

  const t = DICT[lang] ?? DICT.sv;
  
  

  const crumbs = useMemo(() => {
    const parts = currentPath.split("/").filter(Boolean);
    const out = [{ name: t.root, path: "/" }];
    let acc = "";
    for (const p of parts) {
      acc += "/" + p;
      out.push({ name: p, path: acc });
    }
    return out;
  }, [currentPath, t.root]);

  
  const refresh = useCallback(async (path) => {
  setLoading(true);
  setError("");
  try {
    const list = await listFiles(path);
    setFiles(Array.isArray(list) ? list : []);
  } catch (e) {
    setError(e.message || t.fetchError);
  } finally {
    setLoading(false);
  }
}, [t.fetchError]);

  useEffect(() => {
    refresh("/");
  }, [refresh]);

  
  useEffect(() => {
    setSelectedTags([]);
  }, [currentPath]);

  const goTo = async (path) => {
    const normalized = path === "" ? "/" : path;
    setCurrentPath(normalized);
    await refresh(normalized);
  };

  const goUp = async () => {
    if (currentPath === "/") return;
    const parts = currentPath.split("/").filter(Boolean);
    parts.pop();
    const parent = "/" + parts.join("/");
    await goTo(parent === "/" ? "/" : parent);
  };

  const handleDelete = async (fileId, fileName = "") => {
    setError("");
    try {
      if (!window.confirm(`${t.confirmdelete} "${fileName || "fil"}"?`)) return;
      await deleteFile(fileId);
      await refresh(currentPath);
    } catch (e) {
      console.error(e);
      setError(e.message || "Kunde inte ta bort fil.");
    }
  };

  const openFolderPath = (folderName) => {
    const next =
      currentPath === "/" ? `/${folderName}` : `${currentPath}/${folderName}`;
    goTo(next);
  };


  const openUploadModal = (mode) => {
    setUploadMode(mode);
    setUploadTags("");
    setSelectedFiles([]);
    setUploadOpen(true);
  };

  const closeUploadModal = () => {
    setUploadOpen(false);
    setSelectedFiles([]);
    setUploadTags("");
  };

  const pickFiles = () => {
    if (uploadMode === "file") fileInputRef.current?.click();
    else folderInputRef.current?.click();
  };

  const onPickChange = (e) => {
    const list = Array.from(e.target.files || []);
    setSelectedFiles(list);
  };

  const submitUpload = async () => {
    if (selectedFiles.length === 0) {
      setError(t.chooseonefile);
      return;
    }

    setError("");
    setLoading(true);
    try {
      if (uploadMode === "file") {
        const f = selectedFiles[0];
        await uploadFile({ path: currentPath, file: f, tags: uploadTags });
      } else {
        for (const f of selectedFiles) {
          const rel = f.webkitRelativePath || f.name;
          await uploadFile({
            path: currentPath,
            file: f,
            relativePath: rel,
            tags: uploadTags,
          });
        }
      }

      await refresh(currentPath);
      closeUploadModal();
    } catch (e) {
      console.error(e);
      setError(e.message || "Uppladdning misslyckades.");
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (folderInputRef.current) folderInputRef.current.value = "";
    }
  };


  const BACKEND = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
  const absoluteUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    if (url.startsWith("/")) return `${BACKEND}${url}`;
    return `${BACKEND}/${url}`;
  };


  const allTags = useMemo(() => {
    const set = new Set();
    for (const f of safeFiles) {
      if (f.type !== "file") continue;
      for (const t of f.tags || []) {
        const clean = String(t).trim();
        if (clean) set.add(clean);
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [safeFiles]);

  
  const filteredFiles = useMemo(() => {
    if (selectedTags.length === 0) return safeFiles;

    const hasAny = (fileTags) => selectedTags.some((t) => fileTags.includes(t));
    const hasAll = (fileTags) => selectedTags.every((t) => fileTags.includes(t));

    return safeFiles.filter((f) => {
      if (f.type === "folder") return true;
      const fileTags = (f.tags || []).map((t) => String(t).trim());
      return tagMatchMode === "all" ? hasAll(fileTags) : hasAny(fileTags);
    });
  }, [safeFiles, selectedTags, tagMatchMode]);

  return (
    <div className="page">
      <div className="page-bg" style={{ backgroundImage: `url(${natureBg})` }} />

      <div className="page-content file-file-page">
        <header
          className="file-file-header"
          onClick={() => navigate("/homelogin")}
        >
          Scoutkåren Vasa Korsfarare
        </header>

        <aside className="file-sidebar">
          <img
            src={require("./img/treetop.png")}
            alt="tree top"
            className="file-sidebar-tree"
          />

          <div className="file-sidebar-logo-wrapper">
            <img
              src={require("./img/svk.png")}
              alt="logo"
              className="file-sidebar-logo"
            />
            <div className="file-sidebar-logo-bubble">{t.welcome}</div>
          </div>

          <button className="file-menu-btn" onClick={() => navigate("/file")}>
            {t.files}
          </button>
          <button className="file-menu-btn" onClick={() => navigate("/todo")}>
            {t.todo}
          </button>
          <button
            className="file-menu-btn"
            onClick={() => navigate("/inventory")}
          >
            {t.inventory}
          </button>
          <button
            className="file-menu-btn"
            onClick={() => navigate("/homelogin")}
          >
            {t.back}
          </button>
        </aside>

        <main className="file-file-content">
          <h2 className="file-file-title">{t.fileSystem}</h2>

          <div className="file-breadcrumb-row">
            <div className="file-breadcrumbs">
              {crumbs.map((c, idx) => (
                <span key={c.path}>
                  <button
                    type="button"
                    className="file-breadcrumb"
                    onClick={() => goTo(c.path)}
                    disabled={c.path === currentPath || loading}
                  >
                    {c.name}
                  </button>
                  {idx < crumbs.length - 1 ? " / " : ""}
                </span>
              ))}
            </div>

            <button
              type="button"
              className="file-up-btn"
              onClick={goUp}
              disabled={currentPath === "/" || loading}
              title={t.gouptitle}
            >
              {t.up}
            </button>
          </div>

          <div className="file-file-toolbar">
            <button
              type="button"
              className="file-upload-btn"
              onClick={() => openUploadModal("file")}
              disabled={loading}
            >
              {t.uploadFile}
            </button>

            <button
              type="button"
              className="file-upload-btn secondary"
              onClick={() => openUploadModal("folder")}
              disabled={loading}
            >
              {t.uploadFolder}
            </button>

            <span className="file-file-count">
              {loading ? t.loading : `${filteredFiles.length} ${t.items}`}
            </span>
          </div>

          
            <div className="file-tag-filter">
              <div className="file-tag-filter-header">
                <button
                  type="button"
                  className="file-tag-filter-toggle"
                  onClick={() => setTagFilterOpen((v) => !v)}
                  disabled={loading || allTags.length === 0}
                >
                  {tagFilterOpen ? t.hidetagfilter : t.showtagfilter }
                  {selectedTags.length > 0 ? ` (${selectedTags.length})` : ""}
                </button>

                <button
                  type="button"
                  className="file-tag-filter-clear"
                  onClick={() => setSelectedTags([])}
                  disabled={selectedTags.length === 0}
                  title="Rensa valda tags"
                >
                  {t.remove}
                </button>
              </div>

              {tagFilterOpen && (
                <>
                  <label className="file-tag-filter-label">{t.choosetags}</label>

                  <select
                    className="file-tag-filter-select"
                    multiple
                    value={selectedTags}
                    onChange={(e) => {
                    const values = Array.from(e.target.selectedOptions).map((o) => o.value);
                    setSelectedTags(values);
                  }}
                  disabled={loading || allTags.length === 0}
                  size={6}   
                >
                  {allTags.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>

                <div className="file-tag-filter-row">
                  <label className="file-tag-filter-mode">
                    <input
                      type="checkbox"
                      checked={tagMatchMode === "all"}
                      onChange={(e) => setTagMatchMode(e.target.checked ? "all" : "any")}
                      disabled={selectedTags.length === 0}
                    />
                    {t.matchtags}
                  </label>

                  
                </div>
              </>
            )}
          </div>

          {error && (
            <div className="file-error">
              <strong>Fel:</strong> {error}
            </div>
          )}

          
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: "none" }}
            onChange={onPickChange}
          />

          <input
            ref={folderInputRef}
            type="file"
            style={{ display: "none" }}
            webkitdirectory="true"
            directory="true"
            multiple
            onChange={onPickChange}
          />

          
          {uploadOpen && (
            <div className="file-modal-backdrop" onClick={closeUploadModal}>
              <div className="file-modal" onClick={(e) => e.stopPropagation()}>
                <div className="file-modal-title">
                  {uploadMode === "file" ? t.uploadFile : t.uploadFolder}
                </div>

                <div className="file-modal-row">
                  <button
                    type="button"
                    className="file-modal-pick"
                    onClick={pickFiles}
                    disabled={loading}
                  >
                     {uploadMode === "file" ? t.pickFile : t.pickFolder}
                  </button>

                  <div className="file-modal-picked">
                    {selectedFiles.length === 0
                      ? t.nothingChosen
                      : uploadMode === "file"
                      ? selectedFiles[0].name
                      : `${selectedFiles.length} ${t.filesChosen}`}
                  </div>
                </div>

                <div className="file-modal-row-col">
                  <label className="file-modal-label">Tags</label>
                  <input
                    className="file-modal-input"
                    type="text"
                    placeholder={t.tagPlaceholder}
                    value={uploadTags}
                    onChange={(e) => setUploadTags(e.target.value)}
                    disabled={loading}
                  />
                  <small className="file-modal-hint">
                    {t.commaSeparated}: <b>{t.tagex}</b>
                  </small>
                </div>

                <div className="file-modal-actions">
                  <button
                    type="button"
                    className="file-modal-cancel"
                    onClick={closeUploadModal}
                    disabled={loading}
                  >
                    {t.cancel}
                  </button>
                  <button
                    type="button"
                    className="file-modal-submit"
                    onClick={submitUpload}
                    disabled={loading}
                  >
                    {uploadMode === "file" ? t.uploadFile : t.uploadFolder}
                  </button>
                </div>
              </div>
            </div>
          )}

          
          <div className="file-table-wrap">
            <table className="file-table">
              <thead>
                <tr>
                  <th>{t.name}</th>
                  <th>{t.date}</th>
                  <th>{t.tags}</th>
                  <th style={{ textAlign: "right" }}>{t.actions}</th>
                </tr>
              </thead>

              <tbody>
                {filteredFiles.map((f) => (
                  <tr key={`${f.type}-${f.id}`}>
                    <td>
                      <span className="file-icon">
                        {f.type === "folder" ? "📁" : "📄"}
                      </span>{" "}
                      {f.type === "folder" ? (
                        <button
                          type="button"
                          className="file-folder-link"
                          onClick={() => openFolderPath(f.name)}
                          disabled={loading}
                          title="Öppna mapp"
                        >
                          {f.name}
                        </button>
                      ) : (
                        <span className="file-name">{f.name}</span>
                      )}
                    </td>

                    <td>{f.created_at || f.uploaded_at || "—"}</td>

                    <td>
                      {f.type === "file"
                        ? f.tags?.length
                          ? f.tags.join(", ")
                          : "—"
                        : "—"}
                    </td>

                    <td style={{ textAlign: "right" }}>
                      {f.type === "file" && f.download_url && (
                        <a
                          className="file-download-btn"
                          href={absoluteUrl(f.download_url)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          ⬇️
                        </a>
                      )}

                      {f.type === "file" && (
                        <button
                          className="file-file-delete"
                          type="button"
                          onClick={() => handleDelete(f.id, f.name)}
                          disabled={loading}
                        >
                          🗑️
                        </button>
                      )}

                      {f.type === "folder" && (
                        <button
                          className="file-file-delete"
                          type="button"
                          onClick={async () => {
                            try {
                              setError("");
                              setLoading(true);
                              await deleteFolder(f.id);
                              await refresh(currentPath);
                            } catch (e) {
                              setError(e.message || "Kunde inte ta bort mapp.");
                            } finally {
                              setLoading(false);
                            }
                          }}
                          disabled={loading}
                        >
                          🗑️
                        </button>
                      )}
                    </td>
                  </tr>
                ))}

                {!loading && filteredFiles.length === 0 && (
                  <tr>
                    <td colSpan={4} className="file-file-empty">
                      {t.noFiles}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </main>
      </div>
    </div>
  );
}

export default FileManagerPage;
